import { FC, useId, useMemo, useState, createElement } from "react";
import { Search, X } from "lucide-react";

export interface GroupOption {
	id: number;
	name: string;
	memberCount?: number;
}

export interface GroupsAudienceCardProps {
	groupIds: number[];
	onChange: (groupIds: number[]) => void;
	groups: GroupOption[];
	isLoading?: boolean;
	isError?: boolean;
	disabled?: boolean;
	title?: string;
	emptyHint?: string;
	helperText?: string;
	headingLevel?: 2 | 3 | 4;
}

const GroupsAudienceCard: FC<GroupsAudienceCardProps> = ({
	groupIds,
	onChange,
	groups,
	isLoading = false,
	isError = false,
	disabled = false,
	title = "Audience groups",
	emptyHint = "Visible to everyone",
	helperText = "Restrict who can see this content. Leave empty to show to everyone.",
	headingLevel = 3
}) => {
	const reactId = useId();
	const titleId = `${reactId}-title`;
	const searchInputId = `${reactId}-search`;
	const resultsId = `${reactId}-results`;
	const [search, setSearch] = useState("");

	const groupsById = useMemo(() => {
		const map = new Map<number, GroupOption>();
		for (const g of groups) {
			if (map.has(g.id) && process.env.NODE_ENV !== "production") {
				// Surface backend / caller bugs early instead of silently
				// overwriting a duplicate id.
				// eslint-disable-next-line no-console
				console.warn("GroupsAudienceCard: duplicate group id", g.id);
			}
			map.set(g.id, g);
		}
		return map;
	}, [groups]);
	const selectedIdSet = useMemo(() => new Set(groupIds), [groupIds]);

	const term = search.trim().toLowerCase();
	const filtered = useMemo(
		() =>
			groups.filter(
				(g) =>
					!selectedIdSet.has(g.id) &&
					(term.length === 0 || g.name.toLowerCase().includes(term))
			),
		[groups, selectedIdSet, term]
	);

	const add = (id: number) => {
		if (disabled) return;
		onChange([...groupIds, id]);
		setSearch("");
	};

	const remove = (id: number) => {
		if (disabled) return;
		onChange(groupIds.filter((existing) => existing !== id));
	};

	const heading = createElement(
		`h${headingLevel}`,
		{
			id: titleId,
			className: "font-heading text-lg font-medium text-foreground"
		},
		title
	);

	return (
		<section
			aria-labelledby={titleId}
			className="rounded-xl border border-border bg-subtle shadow-sm transition-colors"
		>
			<div className="p-6">
				<header className="flex items-start justify-between gap-3 mb-1">
					{heading}
					<span
						role="status"
						aria-live="polite"
						aria-atomic="true"
						className={[
							"inline-flex items-center rounded-full text-xs font-semibold px-2.5 py-1 whitespace-nowrap",
							groupIds.length === 0
								? "bg-emerald-100 text-emerald-800 ring-1 ring-inset ring-emerald-300"
								: "bg-primary text-primary-foreground"
						].join(" ")}
					>
						{groupIds.length === 0
							? emptyHint
							: `${groupIds.length} group${groupIds.length === 1 ? "" : "s"} selected`}
					</span>
				</header>
				<p className="text-[13px] text-muted-foreground mb-4">{helperText}</p>

				<div className="flex flex-col gap-4">
					{isError && (
						<p role="alert" className="text-sm text-red-600">
							Failed to load groups. Please try again.
						</p>
					)}

					{groupIds.length > 0 && (
						<ul className="flex flex-wrap gap-2" aria-label="Selected groups">
							{groupIds.map((id) => {
								const group = groupsById.get(id);
								const label = group ? group.name : `Group #${id}`;
								const isOrphan = !group;
								return (
									<li key={id}>
										<span
											className={[
												"inline-flex items-center gap-1.5 rounded-full text-sm px-3 py-1",
												isOrphan
													? "bg-amber-100 text-amber-800 ring-1 ring-inset ring-amber-300"
													: "bg-primary/10 text-primary"
											].join(" ")}
											title={
												isOrphan
													? "This group is no longer available. Remove it or wait for the list to load."
													: undefined
											}
										>
											{label}
											{isOrphan && (
												<span className="text-[11px] font-medium uppercase tracking-wide opacity-75">
													unavailable
												</span>
											)}
											<button
												type="button"
												aria-label={
													isOrphan
														? `Remove ${label} (unavailable)`
														: `Remove ${label}`
												}
												onClick={() => remove(id)}
												disabled={disabled}
												className={[
													"transition-colors disabled:opacity-50",
													isOrphan
														? "hover:text-amber-900"
														: "hover:text-primary/70"
												].join(" ")}
											>
												<X className="h-3.5 w-3.5" />
											</button>
										</span>
									</li>
								);
							})}
						</ul>
					)}

					<div className="flex flex-col gap-1.5">
						<label
							htmlFor={searchInputId}
							className="text-sm font-medium text-zinc-600"
						>
							Add groups
						</label>
						<div className="relative">
							<Search
								size={16}
								aria-hidden="true"
								className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
							/>
							<input
								id={searchInputId}
								type="text"
								value={search}
								onChange={(e) => setSearch(e.target.value)}
								onKeyDown={(e) => {
									if (e.key === "Enter") {
										// Stop the parent form from submitting when the user
										// presses Enter while filtering groups. If the filter has
										// narrowed to exactly one match, treat Enter as a quick
										// add for keyboard ergonomics.
										e.preventDefault();
										if (filtered.length === 1) add(filtered[0].id);
									}
								}}
								placeholder={
									isLoading ? "Loading groups…" : "e.g. Store managers"
								}
								disabled={disabled || isLoading}
								aria-controls={search.length > 0 ? resultsId : undefined}
								aria-expanded={search.length > 0}
								className="rounded-md border border-input bg-white pl-9 pr-4 py-2 text-base w-full ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 placeholder:text-muted-foreground disabled:bg-muted disabled:cursor-not-allowed disabled:opacity-50"
							/>
						</div>
						<p className="sr-only" aria-live="polite">
							{search.length === 0
								? ""
								: filtered.length === 0
									? "No groups found"
									: `${filtered.length} group${filtered.length === 1 ? "" : "s"} found`}
						</p>
					</div>

					{search.length > 0 && (
						<ul
							id={resultsId}
							aria-label="Group search results"
							className="border border-border rounded-lg bg-white shadow-sm max-h-[220px] overflow-y-auto divide-y divide-border"
						>
							{filtered.length === 0 ? (
								<li>
									<p
										role="status"
										className="text-sm text-muted-foreground px-3 py-2"
									>
										No groups found
									</p>
								</li>
							) : (
								filtered.map((group) => (
									<li key={group.id}>
										<button
											type="button"
											onClick={() => add(group.id)}
											disabled={disabled}
											className="flex items-center justify-between w-full px-4 py-3 text-sm hover:bg-muted text-left disabled:opacity-50"
										>
											<span className="font-medium text-foreground">
												{group.name}
											</span>
											{typeof group.memberCount === "number" && (
												<span className="text-xs text-muted-foreground">
													{group.memberCount} member
													{group.memberCount === 1 ? "" : "s"}
												</span>
											)}
										</button>
									</li>
								))
							)}
						</ul>
					)}
				</div>
			</div>
		</section>
	);
};

export default GroupsAudienceCard;
